import {
  useCompensationConfiguration,
  useEmployee,
  useEmployments,
} from '@omnipresentgroup/domains';
import {
  getCurrencyInputStep,
  toSentenceCase,
} from '@omnipresentgroup/helpers';
import {
  CompensationConfigRecurrence,
  CompensationSource,
  type CompensationVariantPaymentConfig,
} from '@omnipresentgroup/types';
import { getCountryCode } from 'countries-list';
import type { JSONSchema7 } from 'json-schema';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';

import type { AddVariantPaymentFormFields } from '../types';

const EMPTY_CONFIG_ARRAY: CompensationVariantPaymentConfig[] = [];

const getStartOfMonth = (date: DateTime) => date.startOf('month').toISODate();

const getMinEffectiveDate = (
  employmentStartDate: string,
  isRecurring: boolean,
) => {
  const employmentDay = DateTime.fromISO(employmentStartDate).startOf('day');
  if (isRecurring) {
    const threeMonthsAgo = DateTime.now().minus({ month: 3 }).startOf('day');
    return getStartOfMonth(DateTime.max(employmentDay, threeMonthsAgo));
  }
  const today = DateTime.now().startOf('day');
  return getStartOfMonth(DateTime.max(employmentDay, today));
};

const getMaxEffectiveDate = () => {
  return getStartOfMonth(DateTime.now().plus({ year: 2 }));
};

export const useAddVariantPaymentSchema = ({
  employeeId,
  formValues,
}: {
  employeeId: string;
  formValues: AddVariantPaymentFormFields;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: Boolean(employeeId),
  });

  const { data: employments, isLoading: isLoadingEmployments } = useEmployments(
    {
      userId: employee?.userId || '',
      enabled: !!employee?.userId,
    },
  );

  const employmentStartDate = useMemo(
    () =>
      employments?.find(
        (employment) => employment.id === employee?.currentEmploymentId,
      )?.startDate,
    [employments, employee?.currentEmploymentId],
  );

  const employeeCurrency = useMemo(
    () => employee?.employmentDetails?.annualBaseSalary?.currency || '',
    [employee?.employmentDetails?.annualBaseSalary?.currency],
  );

  const employeeCountryCode = useMemo(
    () =>
      ((employee?.country?.name && getCountryCode(employee?.country?.name)) ||
        '') as string,
    [employee?.country?.name],
  );

  const { data: compensationConfig, isLoading: isLoadingCompensationConfig } =
    useCompensationConfiguration({
      countryCode: employeeCountryCode,
      source: CompensationSource.MONTH_ON_MONTH,
      enabled: Boolean(employeeCountryCode),
    });

  const itemsConfig = useMemo(
    () => compensationConfig?.variantPaymentsTypes || EMPTY_CONFIG_ARRAY,
    [compensationConfig?.variantPaymentsTypes],
  );

  const typeOptions = useMemo(() => {
    const types = itemsConfig.map((item) => item.type);
    const uniqueTypes = [...new Set(types)];
    return uniqueTypes
      .map((type) => ({ const: type, title: toSentenceCase(type) }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [itemsConfig]);

  const subtypeOptions = useMemo(() => {
    return itemsConfig
      .filter((item) => item.type === formValues?.type && item.subtype)
      .map((item) => ({
        const: item.subtype,
        title: item.name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [formValues?.type, itemsConfig]);

  const recurrenceOptions = useMemo(() => {
    return (
      itemsConfig
        .find(
          (item) =>
            item.type === formValues?.type &&
            item.subtype === formValues?.subtype,
        )
        ?.recurrence.map((rec) => ({
          const: rec,
          title: toSentenceCase(rec),
        }))
        .sort((a, b) => a.title.localeCompare(b.title)) || []
    );
  }, [formValues?.subtype, formValues?.type, itemsConfig]);

  const isRecurring = useMemo(
    () =>
      formValues?.recurrence &&
      formValues?.recurrence !== CompensationConfigRecurrence.ONE_OFF,
    [formValues?.recurrence],
  );

  const minEndDate = useMemo(() => {
    return formValues?.effectiveDate
      ? DateTime.fromISO(formValues?.effectiveDate)
          .plus({ month: 1 })
          .startOf('month')
          .toISODate()
      : undefined;
  }, [formValues?.effectiveDate]);

  useEffect(() => {
    setSchema({
      type: 'object',
      description: '',
      required: [
        ...(typeOptions.length > 0 ? ['type'] : []),
        ...(subtypeOptions.length > 0 ? ['subtype'] : []),
        'amount',
        ...(recurrenceOptions.length > 0 ? ['recurrence'] : []),
        'effectiveDate',
        ...(isRecurring && !formValues?.untilEndOfContract ? ['endDate'] : []),
        'reason',
      ],
      properties: {
        type: {
          title: 'Type',
          type: 'string',
          ...(typeOptions?.length > 0 ? { oneOf: typeOptions } : {}),
        },
        subtype: {
          title: 'Subtype',
          type: 'string',
          ...(subtypeOptions?.length > 0 ? { oneOf: subtypeOptions } : {}),
        },
        recurrence: {
          title: 'Recurrence',
          type: 'string',
          ...(recurrenceOptions?.length > 0
            ? { oneOf: recurrenceOptions }
            : {}),
        },
        amount: {
          type: 'object',
          required: ['currency', 'amount'],
          properties: {
            currency: {
              title: 'Currency',
              type: 'string',
              readOnly: true,
              default: employeeCurrency,
            },
            amount: {
              title: 'Gross amount',
              type: 'number',
              minimum: 0.01,
            },
          },
        },
        effectiveDate: {
          title: `${isRecurring ? 'Starting' : 'Effective'} month`,
          type: 'string',
          format: 'date',
        },
        endDate: {
          title: 'Ending month',
          type: 'string',
          format: 'date',
        },
        untilEndOfContract: {
          title: 'Until the end of the contract',
          type: 'boolean',
        },
        reason: {
          title: 'Reason',
          type: 'string',
        },
        additionalNotes: {
          title: 'Additional notes',
          type: 'string',
        },
      },
    });

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      'ui:order': [
        'type',
        'subtype',
        'amount',
        'recurrence',
        'effectiveDate',
        'endDate',
        'untilEndOfContract',
        'reason',
        'additionalNotes',
      ],
      type: {
        'ui:widget': typeOptions?.length > 0 ? 'radio' : 'hidden',
      },
      subtype: {
        'ui:widget': subtypeOptions?.length > 0 ? undefined : 'hidden',
      },
      recurrence: {
        'ui:widget': recurrenceOptions?.length > 0 ? 'radio' : 'hidden',
        'ui:options': {
          tooltip:
            'Recurring items needs to be regular for a minimum of 2 months. If you want it to occur once, please select ‘One off’.',
        },
      },
      amount: {
        'ui:columns': {
          columns: 6,
        },
        'ui:title': 'Gross amount',
        currency: {
          'ui:label': false,
          'ui:span': 2,
        },
        amount: {
          'ui:label': false,
          'ui:span': 4,
          'ui:options': {
            step: getCurrencyInputStep(employeeCurrency),
          },
        },
      },
      effectiveDate: {
        'ui:options': {
          showMonthYearPicker: true,
          dateFormat: 'MMM yyyy',
          minDate: employmentStartDate
            ? getMinEffectiveDate(employmentStartDate, !!isRecurring)
            : undefined,
          maxDate: getMaxEffectiveDate(),
        },
      },
      endDate: {
        'ui:widget':
          isRecurring && !formValues?.untilEndOfContract ? undefined : 'hidden',
        'ui:options': {
          showMonthYearPicker: true,
          dateFormat: 'MMM yyyy',
          minDate: minEndDate,
        },
      },
      untilEndOfContract: {
        'ui:widget': isRecurring ? undefined : 'hidden',
      },
      reason: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 1,
        },
      },
      additionalNotes: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 1,
        },
      },
    });
  }, [
    employeeCurrency,
    employmentStartDate,
    formValues.effectiveDate,
    formValues.recurrence,
    formValues?.untilEndOfContract,
    isRecurring,
    minEndDate,
    recurrenceOptions,
    subtypeOptions,
    typeOptions,
  ]);

  return {
    schema,
    uiSchema,
    isloading:
      Boolean(employeeId) &&
      (isLoadingEmployee ||
        isLoadingEmployments ||
        isLoadingCompensationConfig),
  };
};
